import {
  Delete as DeleteIcon,
  LocalOffer as DiscountIcon,
  Download as DownloadIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import usePermissions from "../hooks/usePermissions";
import { getAcademicYears, getBanks } from "../Services/configService";
import {
  applyAssignmentDiscount,
  assignExtraFeeToStudent,
  assignFeesToStudent,
  deletePayment,
  getFeeStructures,
  getPaymentHistory,
  getStudentFees,
  recordPayment,
} from "../Services/fees";
import { getStudentById } from "../Services/student";
import {
  selectSchoolProfile,
  selectSchoolProfileLoading,
} from "../store/slices/schoolProfileSlice";
import {
  getActiveAcademicYearRow,
  isDateInRange,
} from "../utils/academicYearUtils";
import { printPaymentReceipt } from "../utils/printUtils";
import BannerMessage from "./BannerMessage";
import CustomBreadcrumbs from "./Breadcrumbs";
import ConfirmationDialog from "./ConfirmationDialog";
import DateField from "./DateField";
import DiscountDialog from "./DiscountDialog";
import Loader from "./Loader";
import PaymentDialog from "./PaymentDialog";

const StudentFeesView = ({ hideHeader = false }) => {
  const location = useLocation();
  const { id } = useParams();
  const studentId = id;
  const schoolProfile = useSelector(selectSchoolProfile);
  const schoolProfileLoading = useSelector(selectSchoolProfileLoading);
  const [studentInfo, setStudentInfo] = useState(null);
  const schoolName =
    studentInfo?.institution_name ||
    schoolProfile?.institution_name ||
    "iSchool Management System";
  const [studentFees, setStudentFees] = useState({});
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [summary, setSummary] = useState({});
  const [selectedAcademicYear, setSelectedAcademicYear] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amountPaid: "",
    paymentMethod: "CASH",
    bankId: "",
    paymentDate: moment().format("YYYY-MM-DD"),
    transactionReference: "",
    notes: "",
  });
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountData, setDiscountData] = useState({ amount: "", reason: "" });
  const [showBanner, setShowBanner] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerType, setBannerType] = useState("success");
  const academicYearsLoadedRef = useRef(false);

  // Delete payment confirmation logic
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Advance payment dialog state
  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false);
  const [advanceAcademicYear, setAdvanceAcademicYear] = useState("");
  const [advanceFeeStructures, setAdvanceFeeStructures] = useState([]);
  const [selectedAdvanceFeeStructureId, setSelectedAdvanceFeeStructureId] =
    useState("");
  const [advanceLoading, setAdvanceLoading] = useState(false);
  const [advanceCourseId, setAdvanceCourseId] = useState(null);
  const [advanceNextClassYear, setAdvanceNextClassYear] = useState(null);
  const [isGraduatingStudent, setIsGraduatingStudent] = useState(false);
  const { hasPermission, hasRole } = usePermissions();

  // Extra Fee Dialog State
  const [extraFeeDialogOpen, setExtraFeeDialogOpen] = useState(false);
  const [extraFeeLoading, setExtraFeeLoading] = useState(false);
  const [extraFeeData, setExtraFeeData] = useState({
    feeType: "",
    amount: "",
    dueDate: moment().format("YYYY-MM-DD"),
    description: "",
  });

  const payment_access =
    hasPermission("FEES", "EDIT") ||
    hasPermission("FEES", "PAYMENT") ||
    hasRole("SUPER_ADMIN");
  const dashboard_access =
    hasPermission("DASHBOARD", "VIEW") || hasRole("SUPER_ADMIN");
  const student_access =
    hasPermission("STUDENT", "CREATE") || hasRole("SUPER_ADMIN");
  const delete_access = hasRole("SUPER_ADMIN") || hasRole("ADMIN");

  const fetchAcademicYears = useCallback(async () => {
    if (academicYearsLoadedRef.current) return;
    academicYearsLoadedRef.current = true;
    try {
      const response = await getAcademicYears();
      if (response.success && response.data) {
        const years = response.data;
        setAcademicYears(years);

        // Prefer centralized helper to determine active academic year row
        const activeRow = getActiveAcademicYearRow(years);
        if (activeRow) {
          setSelectedAcademicYear(activeRow.academic_year);
        } else if (years.length > 0) {
          setSelectedAcademicYear(years[0].academic_year);
        }
      } else {
        setBannerMessage("Failed to fetch academic years");
        setBannerType("error");
        setShowBanner(true);
      }
    } catch (error) {
      console.error("Error in fetchAcademicYears:", error.message);
      setBannerMessage("Failed to fetch academic years");
      setBannerType("error");
      setShowBanner(true);
      academicYearsLoadedRef.current = false; // Reset on error so it can retry
    }
  }, []);

  const fetchBanks = useCallback(async () => {
    try {
      const response = await getBanks(true);
      if (response?.status === "success") {
        setBanks(Array.isArray(response.data) ? response.data : []);
      } else {
        setBanks([]);
      }
    } catch (error) {
      console.error("Error in fetchBanks:", error.message);
      setBanks([]);
    }
  }, []);

  const fetchStudentFees = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getStudentFees(studentId, selectedAcademicYear);
      if (response.error) {
        setBannerMessage(response.error);
        setBannerType("error");
        setShowBanner(true);
      } else {
        setStudentFees(response.data.studentFees || {});
        setSummary(response.data.summary || {});
      }
    } catch (error) {
      console.error("Error in fetchStudentFees:", error.message);
      setBannerMessage("Failed to fetch student fees");
      setBannerType("error");
      setShowBanner(true);
    } finally {
      setLoading(false);
    }
  }, [studentId, selectedAcademicYear]);

  const fetchStudentProgressionMeta = useCallback(async () => {
    try {
      const response = await getStudentById(studentId, selectedAcademicYear);
      if (response?.error || !response?.data) {
        setIsGraduatingStudent(false);
        return;
      }

      const student = response.data;
      setStudentInfo(student);
      const classToken = String(student.class || "").split("_")[1];
      const classYear = Number.parseInt(classToken, 10);
      const durationYears = Number.parseInt(student.duration_years, 10);

      if (Number.isNaN(classYear) || Number.isNaN(durationYears)) {
        setIsGraduatingStudent(false);
        return;
      }

      setIsGraduatingStudent(classYear >= durationYears);
    } catch (error) {
      console.error("Error in fetchStudentProgressionMeta:", error.message);
      setIsGraduatingStudent(false);
    }
  }, [studentId, selectedAcademicYear]);

  const fetchPaymentHistory = useCallback(async () => {
    try {
      const response = await getPaymentHistory(studentId, {
        academicYear: selectedAcademicYear,
      });
      if (response.error) {
        setBannerMessage(response.error);
        setBannerType("error");
        setShowBanner(true);
      } else {
        setPaymentHistory(response.data || []);
      }
    } catch (error) {
      console.error("Error in fetchPaymentHistory:", error.message);
      setBannerMessage("Failed to fetch payment history");
      setBannerType("error");
      setShowBanner(true);
    }
  }, [studentId, selectedAcademicYear]);

  useEffect(() => {
    fetchAcademicYears();
    fetchBanks();
  }, [fetchAcademicYears, fetchBanks]);

  useEffect(() => {
    if (selectedAcademicYear) {
      fetchStudentFees();
      fetchPaymentHistory();
      fetchStudentProgressionMeta();
    }
  }, [
    studentId,
    selectedAcademicYear,
    fetchStudentFees,
    fetchPaymentHistory,
    fetchStudentProgressionMeta,
  ]);

  const selectedAcademicYearConfig = academicYears.find(
    (year) => year.academic_year === selectedAcademicYear,
  );

  const getDefaultPaymentDateForYearConfig = (yearConfig) => {
    const today = moment().startOf("day");
    const startDate = yearConfig?.start_date
      ? moment(yearConfig.start_date).startOf("day")
      : null;
    const endDate = yearConfig?.end_date
      ? moment(yearConfig.end_date).startOf("day")
      : null;

    if (!startDate?.isValid() || !endDate?.isValid()) {
      return today.format("YYYY-MM-DD");
    }

    if (today.isBefore(startDate, "day")) {
      return today.format("YYYY-MM-DD");
    }

    if (today.isAfter(endDate, "day")) {
      return endDate.format("YYYY-MM-DD");
    }

    return today.format("YYYY-MM-DD");
  };

  const getDefaultPaymentDate = () =>
    getDefaultPaymentDateForYearConfig(selectedAcademicYearConfig);

  const handlePaymentClick = (assignment) => {
    setSelectedAssignment(assignment);
    setPaymentData({
      amountPaid: assignment.balance_due.toString(),
      paymentMethod: "CASH",
      bankId: "",
      paymentDate: getDefaultPaymentDate(),
      transactionReference: "",
      notes: "",
    });
    setPaymentDialogOpen(true);
  };

  const handleDiscountClick = (assignment) => {
    setSelectedAssignment(assignment);
    setDiscountData({
      amount: assignment.discount_amount?.toString() || "",
      reason: assignment.discount_reason || "",
    });
    setDiscountDialogOpen(true);
  };

  const handleOpenExtraFeeDialog = () => {
    setExtraFeeData({
      feeType: "",
      amount: "",
      dueDate: getDefaultPaymentDate(),
      description: "",
    });
    setExtraFeeDialogOpen(true);
  };

  const handleExtraFeeSubmit = async () => {
    if (
      !extraFeeData.feeType ||
      !extraFeeData.amount ||
      !extraFeeData.dueDate
    ) {
      setBannerMessage("Please fill in all required fields.");
      setBannerType("error");
      setShowBanner(true);
      return;
    }

    setExtraFeeLoading(true);
    try {
      const payload = {
        studentId,
        academicYear: selectedAcademicYear,
        ...extraFeeData,
      };

      const response = await assignExtraFeeToStudent(payload);
      if (response.error) {
        setBannerMessage(response.error);
        setBannerType("error");
        setShowBanner(true);
      } else {
        setBannerMessage("Extra fee assigned successfully.");
        setBannerType("success");
        setShowBanner(true);
        setExtraFeeDialogOpen(false);
        fetchStudentFees();
      }
    } catch (error) {
      console.error("Error in handleExtraFeeSubmit:", error);
      setBannerMessage("Failed to assign extra fee.");
      setBannerType("error");
      setShowBanner(true);
    } finally {
      setExtraFeeLoading(false);
    }
  };

  const handlePaymentSubmit = async () => {
    try {
      const response = await recordPayment({
        studentId,
        assignmentId: selectedAssignment.assignment_id,
        amountPaid: Number.parseFloat(paymentData.amountPaid),
        paymentMethod: paymentData.paymentMethod,
        bankId: paymentData.bankId
          ? Number.parseInt(paymentData.bankId, 10)
          : null,
        paymentDate: paymentData.paymentDate,
        transactionReference: paymentData.transactionReference,
        notes: paymentData.notes,
      });

      if (response.error) {
        setBannerMessage(response.error);
        setBannerType("error");
        setShowBanner(true);
      } else {
        setPaymentDialogOpen(false);
        setSelectedAssignment(null);
        setPaymentData({
          amountPaid: "",
          paymentMethod: "CASH",
          bankId: "",
          paymentDate: getDefaultPaymentDate(),
          transactionReference: "",
          notes: "",
        });
        setBannerMessage("Payment recorded successfully");
        setBannerType("success");
        setShowBanner(true);
        fetchStudentFees();
        fetchPaymentHistory();
      }
    } catch (error) {
      console.error("Error in recordPayment:", error.message);
      setBannerMessage("Failed to record payment");
      setBannerType("error");
      setShowBanner(true);
    }
  };

  const handleDiscountSubmit = async () => {
    try {
      const amount = Number.parseFloat(discountData.amount);
      if (Number.isNaN(amount) || amount < 0) {
        setBannerMessage("Invalid discount amount");
        setBannerType("error");
        setShowBanner(true);
        return;
      }
      const response = await applyAssignmentDiscount(
        selectedAssignment.assignment_id,
        {
          studentId: Number.parseInt(studentId),
          discountAmount: amount,
          discountReason: discountData.reason || undefined,
        },
      );
      if (response.error) {
        setBannerMessage(response.error);
        setBannerType("error");
        setShowBanner(true);
      } else {
        setDiscountDialogOpen(false);
        setSelectedAssignment(null);
        setDiscountData({ amount: "", reason: "" });
        setBannerMessage("Fee discount applied successfully");
        setBannerType("success");
        setShowBanner(true);
        fetchStudentFees();
        fetchPaymentHistory();
      }
    } catch (error) {
      console.error("Error applying discount:", error.message);
      setBannerMessage("Failed to apply discount");
      setBannerType("error");
      setShowBanner(true);
    }
  };

  const handleDeletePayment = (payment) => {
    setPaymentToDelete(payment);
    setDeleteConfirmOpen(true);
  };

  const confirmDeletePayment = async () => {
    if (!paymentToDelete) return;

    setDeleteLoading(true);
    try {
      const response = await deletePayment(paymentToDelete.payment_id);
      if (response.error) {
        setBannerMessage(response.error);
        setBannerType("error");
        setShowBanner(true);
      } else {
        setBannerMessage("Payment record deleted successfully");
        setBannerType("success");
        setShowBanner(true);
        fetchStudentFees();
        fetchPaymentHistory();
      }
    } catch (error) {
      console.error("Error deleting payment:", error.message);
      setBannerMessage("Failed to delete payment");
      setBannerType("error");
      setShowBanner(true);
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
      setPaymentToDelete(null);
    }
  };

  const fetchAdvanceFeeStructures = async (
    targetAcademicYear,
    courseId,
    nextClassYear,
  ) => {
    const response = await getFeeStructures({
      academicYear: targetAcademicYear,
      courseId,
      classYear: nextClassYear,
    });
    if (response?.error) {
      setBannerMessage(response.error);
      setBannerType("error");
      setShowBanner(true);
      return [];
    }
    return Array.isArray(response?.data) ? response.data : [];
  };

  const handleOpenAdvanceDialog = async (defaultAcademicYear) => {
    if (!defaultAcademicYear) return;

    const studentProfileResponse = await getStudentById(
      studentId,
      selectedAcademicYear,
    );
    const studentProfile = studentProfileResponse?.data;
    if (studentProfileResponse?.error || !studentProfile) {
      setBannerMessage(
        studentProfileResponse?.error || "Unable to load student profile",
      );
      setBannerType("error");
      setShowBanner(true);
      return;
    }

    const classToken = String(studentProfile.class || "").split("_")[1];
    const currentClassYear = Number.parseInt(classToken, 10);
    const nextClassYear = Number.isNaN(currentClassYear)
      ? null
      : currentClassYear + 1;

    if (!studentProfile.course_id || !nextClassYear) {
      setBannerMessage(
        "Unable to determine student's next class for advance payment",
      );
      setBannerType("error");
      setShowBanner(true);
      return;
    }

    setAdvanceCourseId(studentProfile.course_id);
    setAdvanceNextClassYear(nextClassYear);
    setAdvanceDialogOpen(true);
    setAdvanceLoading(true);
    setAdvanceAcademicYear(defaultAcademicYear);
    setSelectedAdvanceFeeStructureId("");

    const structures = await fetchAdvanceFeeStructures(
      defaultAcademicYear,
      studentProfile.course_id,
      nextClassYear,
    );
    setAdvanceFeeStructures(structures);
    if (structures.length > 0) {
      setSelectedAdvanceFeeStructureId(String(structures[0].fee_structure_id));
    }
    setAdvanceLoading(false);
  };

  const handleAdvanceAcademicYearChange = async (targetAcademicYear) => {
    setAdvanceAcademicYear(targetAcademicYear);
    setAdvanceLoading(true);
    setSelectedAdvanceFeeStructureId("");
    const structures = await fetchAdvanceFeeStructures(
      targetAcademicYear,
      advanceCourseId,
      advanceNextClassYear,
    );
    setAdvanceFeeStructures(structures);
    if (structures.length > 0) {
      setSelectedAdvanceFeeStructureId(String(structures[0].fee_structure_id));
    }
    setAdvanceLoading(false);
  };

  const handleAdvancePaymentSubmit = async () => {
    const fallbackFeeStructureId = advanceFeeStructures[0]?.fee_structure_id;
    const feeStructureId = String(
      selectedAdvanceFeeStructureId || fallbackFeeStructureId || "",
    );
    if (!feeStructureId || !advanceAcademicYear) return;

    setAdvanceLoading(true);

    let nextYearFeesResponse = await getStudentFees(
      studentId,
      advanceAcademicYear,
    );
    if (nextYearFeesResponse?.error) {
      setAdvanceLoading(false);
      setBannerMessage(nextYearFeesResponse.error);
      setBannerType("error");
      setShowBanner(true);
      return;
    }

    let nextYearAssignments = Object.values(
      nextYearFeesResponse?.data?.studentFees || {},
    ).flat();

    const matchesFeeStructure = (assignment) =>
      String(assignment.fee_structure_id) === String(feeStructureId);

    let targetAssignment = nextYearAssignments.find(matchesFeeStructure);

    if (!targetAssignment) {
      const assignResponse = await assignFeesToStudent({
        studentId,
        feeAssignments: [{ feeStructureId, discountAmount: 0 }],
      });

      if (assignResponse?.error) {
        setAdvanceLoading(false);
        setBannerMessage(assignResponse.error);
        setBannerType("error");
        setShowBanner(true);
        return;
      }

      const createdAssignmentIds = Array.isArray(
        assignResponse?.data?.assignmentIds,
      )
        ? assignResponse.data.assignmentIds.map(String)
        : [];

      nextYearFeesResponse = await getStudentFees(
        studentId,
        advanceAcademicYear,
      );
      if (nextYearFeesResponse?.error) {
        setAdvanceLoading(false);
        setBannerMessage(nextYearFeesResponse.error);
        setBannerType("error");
        setShowBanner(true);
        return;
      }

      nextYearAssignments = Object.values(
        nextYearFeesResponse?.data?.studentFees || {},
      ).flat();

      targetAssignment = nextYearAssignments.find(matchesFeeStructure);

      if (!targetAssignment && createdAssignmentIds.length > 0) {
        targetAssignment = nextYearAssignments.find((assignment) =>
          createdAssignmentIds.includes(String(assignment.assignment_id)),
        );
      }
    }

    if (!targetAssignment) {
      setAdvanceLoading(false);
      setBannerMessage(
        "Unable to prepare next-year fee assignment for payment",
      );
      setBannerType("error");
      setShowBanner(true);
      return;
    }

    const advanceYearConfig = academicYears.find(
      (year) => year.academic_year === advanceAcademicYear,
    );

    setSelectedAcademicYear(advanceAcademicYear);
    setSelectedAssignment(targetAssignment);
    setPaymentData({
      amountPaid: Number.parseFloat(
        targetAssignment.balance_due || 0,
      ).toString(),
      paymentMethod: "CASH",
      bankId: "",
      paymentDate: getDefaultPaymentDateForYearConfig(advanceYearConfig),
      transactionReference: "",
      notes: "",
    });
    setAdvanceDialogOpen(false);
    setAdvanceLoading(false);
    setPaymentDialogOpen(true);
  };

  const handleDownloadReceipt = (payment) => {
    printPaymentReceipt(
      payment,
      {
        name: `${payment.first_name || ""} ${payment.last_name || ""}`.trim(),
        admissionNo: payment.admission_no,
      },
      {
        schoolName: payment.institution_name || schoolName,
        schoolAddress: payment.institution_address || "",
        schoolPhone: payment.institution_phone || "",
        schoolEmail: payment.institution_email || "",
      },
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PAID":
        return "success";
      case "PARTIAL":
        return "warning";
      case "PENDING":
        return "error";
      default:
        return "default";
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const formatAdvanceFeeLabel = (feeStructure) => {
    const classLabel =
      feeStructure.class_name || `Class Year ${feeStructure.class_year}`;
    const amountLabel = Number.parseFloat(
      feeStructure.amount || 0,
    ).toLocaleString("en-IN");
    return `${feeStructure.course_name} - ${classLabel} - ₹${amountLabel}`;
  };

  const summaryValueSx = {
    fontSize: { xs: "clamp(1.05rem, 4.8vw, 1.35rem)", sm: "1.25rem" },
    lineHeight: 1.2,
    overflowWrap: "anywhere",
  };

  const summaryLabelSx = {
    fontSize: { xs: "0.72rem", sm: "0.875rem" },
    lineHeight: 1.25,
  };

  const selectedYearFees = Object.values(studentFees).flat();
  const hasSelectedYearAssignments = selectedYearFees.length > 0;
  const allSelectedYearPaid =
    hasSelectedYearAssignments &&
    selectedYearFees.every(
      (fee) => Number.parseFloat(fee.balance_due || 0) <= 0,
    );

  const isSelectedAcademicYearCurrent =
    selectedAcademicYearConfig?.start_date &&
    selectedAcademicYearConfig?.end_date
      ? isDateInRange(
          selectedAcademicYearConfig.start_date,
          selectedAcademicYearConfig.end_date,
        )
      : false;

  const nextAcademicYearOptions = academicYears
    .filter(
      (year) =>
        year.academic_year !== selectedAcademicYear &&
        selectedAcademicYearConfig?.start_date &&
        moment(year.start_date).isAfter(
          moment(selectedAcademicYearConfig.start_date),
          "day",
        ),
    )
    .sort((a, b) => moment(a.start_date).diff(moment(b.start_date)))
    .slice(0, 1);

  const defaultNextAcademicYear =
    nextAcademicYearOptions[0]?.academic_year || "";

  const canShowAdvancePaymentButton =
    payment_access &&
    isSelectedAcademicYearCurrent &&
    allSelectedYearPaid &&
    !isGraduatingStudent;

  if (loading) {
    return <Loader />;
  }

  return (
    <Box>
      {!hideHeader && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            width: "100%",
            mb: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" }, my: 1 }}
          >
            Student Details
          </Typography>
          {(() => {
            const fromFeesTab =
              location.state?.from === "fees-student-fees" ||
              new URLSearchParams(location.search).get("from") === "fees";
            const items = fromFeesTab
              ? [
                  { label: "Dashboard", href: "/dashboard" },
                  { label: "Fees Management", href: "/fees?tab=student-fees" },
                  { label: "Fees Details" },
                ]
              : [
                  {
                    label: "Dashboard",
                    href: "/dashboard",
                    disable: !dashboard_access,
                  },
                  {
                    label: "Students",
                    href: "/student",
                    disable: !student_access,
                  },
                  { label: `${id}`, href: `/student/${id}/view` },
                  { label: "Fees Details" },
                ];
            return <CustomBreadcrumbs items={items} />;
          })()}
        </Box>
      )}

      {/* Academic Year Selector */}
      <Box sx={{ mb: 3 }}>
        <TextField
          select
          label="Academic Year"
          size="small"
          value={selectedAcademicYear}
          onChange={(e) => setSelectedAcademicYear(e.target.value)}
          sx={{ minWidth: { xs: "100%", sm: 220 } }}
        >
          {academicYears.map((year) => (
            <MenuItem key={year.academic_year} value={year.academic_year}>
              {year.academic_year}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
        <Grid size={{ lg: 2, md: 3, sm: 4, xs: 6 }}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <SchoolIcon color="primary" sx={{ mr: { xs: 1, sm: 2 } }} />
                <Box>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    sx={summaryLabelSx}
                  >
                    Total Assigned
                  </Typography>
                  <Typography variant="h6" sx={summaryValueSx}>
                    {formatCurrency(summary.totalAssigned || 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ lg: 2, md: 4, sm: 4, xs: 6 }}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <TrendingUpIcon color="success" sx={{ mr: { xs: 1, sm: 2 } }} />
                <Box>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    sx={summaryLabelSx}
                  >
                    Total Paid
                  </Typography>
                  <Typography variant="h6" sx={summaryValueSx}>
                    {formatCurrency(summary.totalPaid || 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ lg: 2.5, md: 4, sm: 4, xs: 6 }}>
          <Card sx={{ bgcolor: "warning.lighter" }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <WarningIcon color="warning" sx={{ mr: { xs: 1, sm: 2 } }} />
                <Box>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    sx={summaryLabelSx}
                  >
                    Previous Year Pending
                  </Typography>
                  <Typography variant="h6" sx={summaryValueSx}>
                    {formatCurrency(summary.totalPreviousYearPending || 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ lg: 2.5, md: 3, sm: 4, xs: 6 }}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <WarningIcon color="warning" sx={{ mr: { xs: 1, sm: 2 } }} />
                <Box>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    sx={summaryLabelSx}
                  >
                    Current Year Balance
                  </Typography>
                  <Typography variant="h6" sx={summaryValueSx}>
                    {formatCurrency(
                      (summary.totalPending || 0) -
                        (summary.totalPreviousYearPending || 0),
                    )}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ lg: 2, md: 3, sm: 4, xs: 6 }}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ReceiptIcon color="error" sx={{ mr: { xs: 1, sm: 2 } }} />
                <Box>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    sx={summaryLabelSx}
                  >
                    Overdue Items
                  </Typography>
                  <Typography variant="h6" sx={summaryValueSx}>
                    {summary.overdueCount || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Fees Table */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, mb: 0 }}
            >
              Fee Details - {selectedAcademicYear}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {payment_access && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleOpenExtraFeeDialog}
                >
                  Add Extra Fee
                </Button>
              )}
              {canShowAdvancePaymentButton && (
                <Tooltip
                  title={
                    nextAcademicYearOptions.length === 0
                      ? "No future academic year is configured"
                      : "Record payment for next academic year"
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() =>
                        handleOpenAdvanceDialog(defaultNextAcademicYear)
                      }
                      disabled={nextAcademicYearOptions.length === 0}
                    >
                      Advance Payment
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>
          <TableContainer>
            <Table sx={{ "& .MuiTableCell-root": { px: 1.2, py: 1.2 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Fee Type</TableCell>
                  <TableCell align="right">Assigned Amount</TableCell>
                  <TableCell align="right">Discount</TableCell>
                  <TableCell align="right">Final Amount</TableCell>
                  <TableCell align="right">Previous Year Pending</TableCell>
                  <TableCell align="right">Paid Amount</TableCell>
                  <TableCell align="right">Balance Due</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                  {payment_access && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.values(studentFees)
                  .flat()
                  .map((fee) => (
                    <TableRow key={fee.assignment_id}>
                      <TableCell>
                        {fee.fee_type}
                        {fee.previous_year_pending > 0 &&
                          fee.previous_year_from && (
                            <Typography
                              variant="caption"
                              display="block"
                              color="warning.main"
                            >
                              (includes pending from {fee.previous_year_from})
                            </Typography>
                          )}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(fee.assigned_amount)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(fee.discount_amount)}
                        {fee.discount_reason && (
                          <Tooltip
                            title={fee.discount_reason}
                            placement="top"
                            arrow
                          >
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                              sx={{
                                fontStyle: "italic",
                                maxWidth: 120,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                ml: "auto",
                                cursor: "default",
                              }}
                            >
                              {fee.discount_reason}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(fee.final_amount)}
                      </TableCell>
                      <TableCell align="right">
                        {fee.previous_year_pending > 0 ? (
                          <Typography color="warning.main" fontWeight="bold">
                            {formatCurrency(fee.previous_year_pending)}
                          </Typography>
                        ) : (
                          formatCurrency(0)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(fee.total_paid)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">
                          {formatCurrency(fee.balance_due)}
                        </Typography>
                        {fee.previous_year_pending > 0 && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            (Prev: {formatCurrency(fee.previous_year_pending)} +
                            Curr: {formatCurrency(fee.current_year_balance)})
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {moment(fee.due_date).format("DD-MM-YYYY")}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={fee.status}
                          color={getStatusColor(fee.status)}
                          size="small"
                        />
                        {fee.is_overdue && (
                          <Chip
                            label="OVERDUE"
                            color="error"
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </TableCell>
                      {payment_access && (
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {fee.balance_due > 0 && (
                              <Tooltip title="Apply Discount">
                                <span>
                                  <IconButton
                                    color="secondary"
                                    onClick={() => handleDiscountClick(fee)}
                                    size="small"
                                  >
                                    <DiscountIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {fee.balance_due > 0 && (
                              <Tooltip title="Record Payment">
                                <IconButton
                                  color="primary"
                                  onClick={() => handlePaymentClick(fee)}
                                  size="small"
                                >
                                  <PaymentIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
          >
            Payment History - {selectedAcademicYear}
          </Typography>
          <TableContainer>
            <Table sx={{ "& .MuiTableCell-root": { px: 1.2, py: 1.2 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Fee Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Receipt Number</TableCell>
                  <TableCell>Transaction Ref</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentHistory.map((payment) => (
                  <TableRow key={payment.payment_id}>
                    <TableCell>
                      {moment(payment.payment_date).format("DD-MM-YYYY")}
                    </TableCell>
                    <TableCell>{payment.fee_type}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(payment.amount_paid)}
                    </TableCell>
                    <TableCell>{payment.payment_method}</TableCell>
                    <TableCell>{payment.receipt_number}</TableCell>
                    <TableCell>
                      {payment.transaction_reference || "-"}
                    </TableCell>
                    <TableCell>{payment.notes || "-"}</TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <Tooltip title="Download Receipt">
                          <IconButton
                            color="primary"
                            onClick={() => handleDownloadReceipt(payment)}
                            size="small"
                            disabled={schoolProfileLoading}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                        {delete_access &&
                          moment().diff(moment(payment.created_on), "days") <=
                            7 && (
                            <Tooltip title="Delete Payment (Mistake)">
                              <IconButton
                                color="error"
                                onClick={() => handleDeletePayment(payment)}
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Advance Payment Dialog */}
      <Dialog
        open={advanceDialogOpen}
        onClose={() => setAdvanceDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              width: { xs: "calc(100% - 24px)", sm: 560 },
            },
          },
        }}
      >
        <DialogTitle>Advance Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Current academic year is fully paid.
            <br />
            Select next academic year and fee details to continue payment.
          </Typography>

          <TextField
            select
            fullWidth
            size="small"
            label="Academic Year"
            value={advanceAcademicYear}
            onChange={(e) => handleAdvanceAcademicYearChange(e.target.value)}
            sx={{ mb: 2 }}
          >
            {nextAcademicYearOptions.map((year) => (
              <MenuItem key={year.academic_year} value={year.academic_year}>
                {year.academic_year}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            size="small"
            label="Fee Details"
            value={selectedAdvanceFeeStructureId}
            onChange={(e) => setSelectedAdvanceFeeStructureId(e.target.value)}
            disabled={advanceLoading || advanceFeeStructures.length === 0}
          >
            {advanceFeeStructures.map((fs) => (
              <MenuItem
                key={fs.fee_structure_id}
                value={String(fs.fee_structure_id)}
              >
                {formatAdvanceFeeLabel(fs)}
              </MenuItem>
            ))}
          </TextField>

          {!advanceLoading && advanceFeeStructures.length === 0 && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 1, display: "block" }}
            >
              No fee structures available for selected academic year.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setAdvanceDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAdvancePaymentSubmit}
            disabled={advanceLoading || !selectedAdvanceFeeStructureId}
          >
            Continue to Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <PaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        onSubmit={handlePaymentSubmit}
        selectedAssignment={selectedAssignment}
        paymentData={paymentData}
        setPaymentData={setPaymentData}
        banks={banks}
        academicYearStartDate={selectedAcademicYearConfig?.start_date}
        academicYearEndDate={selectedAcademicYearConfig?.end_date}
        formatCurrency={formatCurrency}
      />

      <DiscountDialog
        open={discountDialogOpen}
        onClose={() => setDiscountDialogOpen(false)}
        onSubmit={handleDiscountSubmit}
        selectedAssignment={selectedAssignment}
        discountData={discountData}
        setDiscountData={setDiscountData}
        formatCurrency={formatCurrency}
      />

      <ConfirmationDialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setPaymentToDelete(null);
        }}
        onConfirm={confirmDeletePayment}
        title="Delete Payment Record"
        message={`Are you sure you want to delete the payment of ${paymentToDelete ? formatCurrency(paymentToDelete.amount_paid) : ""} for ${paymentToDelete?.fee_type || ""}? This action will restore the student's balance and cannot be undone.`}
        confirmText="Delete Record"
        loading={deleteLoading}
      />

      <BannerMessage
        open={showBanner}
        message={bannerMessage}
        type={bannerType}
        onClose={() => setShowBanner(false)}
        autoHideDuration={2000}
      />

      {/* Extra Fee Dialog */}
      <Dialog
        open={extraFeeDialogOpen}
        onClose={() => !extraFeeLoading && setExtraFeeDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Extra Fee</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
              <TextField
                fullWidth
                label="Fee Type"
                placeholder="e.g. Fine, ID Card, Event Fee"
                value={extraFeeData.feeType}
                onChange={(e) =>
                  setExtraFeeData({ ...extraFeeData, feeType: e.target.value })
                }
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={extraFeeData.amount}
                onChange={(e) =>
                  setExtraFeeData({ ...extraFeeData, amount: e.target.value })
                }
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
              <DateField
                fullWidth
                label="Due Date"
                name="dueDate"
                value={extraFeeData.dueDate}
                onChange={(e) =>
                  setExtraFeeData({ ...extraFeeData, dueDate: e.target.value })
                }
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={extraFeeData.description}
                onChange={(e) =>
                  setExtraFeeData({
                    ...extraFeeData,
                    description: e.target.value,
                  })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setExtraFeeDialogOpen(false)}
            disabled={extraFeeLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExtraFeeSubmit}
            variant="contained"
            disabled={extraFeeLoading}
          >
            {extraFeeLoading ? "Assigning..." : "Assign Fee"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

StudentFeesView.propTypes = {
  hideHeader: PropTypes.bool,
};

export default StudentFeesView;
